import { useState, useEffect } from "react";
import { Link, Outlet, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Phone, Mail, Facebook, Instagram, ChevronDown, ChevronUp, Settings, ArrowRight } from "lucide-react";

const API = "/api/kennel";

const KennelLayout = () => {
  const [settings, setSettings] = useState(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [cookieAccepted, setCookieAccepted] = useState(() => localStorage.getItem("cookies_accepted") === "true");
  const [dogs, setDogs] = useState([]);
  const [litters, setLitters] = useState([]);
  const [dogsOpen, setDogsOpen] = useState(false);
  const [littersOpen, setLittersOpen] = useState(false);
  const [galleryOpen, setGalleryOpen] = useState(false);
  const [wiedzaOpen, setWiedzaOpen] = useState(false);
  const [hoveredNav, setHoveredNav] = useState(null);
  const location = useLocation();

  useEffect(() => { fetch(`${API}/settings`).then(r => r.json()).then(setSettings); }, []);
  useEffect(() => { fetch(`${API}/dogs`).then(r => r.json()).then(setDogs); }, []);
  useEffect(() => { fetch(`${API}/litters`).then(r => r.json()).then(setLitters); }, []);
  useEffect(() => { setMenuOpen(false); setDogsOpen(false); setLittersOpen(false); setGalleryOpen(false); setWiedzaOpen(false); window.scrollTo(0, 0); }, [location.pathname]);

  useEffect(() => {
    const hide = () => {
      document.querySelectorAll('#emergent-badge, a[href*="emergent.sh"], a[href*="emergentagent"]').forEach(el => {
        if (el.hasAttribute('data-testid') || el.closest('[data-testid]')) return;
        el.remove();
      });
    };
    hide();
    const obs = new MutationObserver(hide);
    obs.observe(document.body, { childList: true, subtree: true });
    return () => obs.disconnect();
  }, []);

  useEffect(() => {
    const fn = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", fn);
    return () => window.removeEventListener("scroll", fn);
  }, []);

  useEffect(() => {
    document.body.style.background = '#0B0B0D';
    return () => { document.body.style.background = ''; };
  }, []);

  // Mouse glow effect
  useEffect(() => {
    const glow = document.createElement('div');
    glow.id = 'cursor-glow';
    glow.style.cssText = 'position:fixed;top:0;left:0;width:600px;height:600px;pointer-events:none;z-index:1;border-radius:50%;background:radial-gradient(circle,rgba(212,175,55,0.12) 0%,rgba(212,175,55,0.06) 25%,rgba(212,175,55,0.02) 50%,transparent 70%);transform:translate(-50%,-50%);transition:opacity 0.4s;opacity:0;will-change:transform;filter:blur(20px);';
    document.body.appendChild(glow);
    let rafId, mx = 0, my = 0, cx = 0, cy = 0;
    const lerp = (a, b, t) => a + (b - a) * t;
    const onMove = (e) => { mx = e.clientX; my = e.clientY; glow.style.opacity = '1'; };
    const onLeave = () => { glow.style.opacity = '0'; };
    const animate = () => {
      cx = lerp(cx, mx, 0.08);
      cy = lerp(cy, my, 0.08);
      glow.style.transform = `translate(${cx - 300}px, ${cy - 300}px)`;
      rafId = requestAnimationFrame(animate);
    };
    rafId = requestAnimationFrame(animate);
    window.addEventListener('mousemove', onMove);
    document.addEventListener('mouseleave', onLeave);
    return () => {
      window.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseleave', onLeave);
      cancelAnimationFrame(rafId);
      glow.remove();
    };
  }, []);
  const nav = [
    { name: "Start", href: "/" },
    { name: "O hodowli", href: "/o-hodowli" },
    { name: "Nasze psy", href: "/psy" },
    { name: "Mioty", href: "/mioty" },
    { name: "Plany hodowlane", href: "/plany" },
    { name: "Szczenięta", href: "/szczenieta" },
    { name: "Galeria", href: "/galeria" },
    { name: "Wiedza o rasie", href: "/owczarek-niemiecki" },
    { name: "Z życia hodowli", href: "/z-zycia-hodowli" },
    { name: "Kontakt", href: "/kontakt" },
  ];

  return (
    <div className="min-h-screen bg-[#0B0B0D] text-[#F5F5F5] overflow-x-hidden">
      {/* Nav */}
      <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${scrolled ? "bg-[#0B0B0D] backdrop-blur-md border-b border-[#2A2A2D]" : "bg-[#0B0B0D]/80 backdrop-blur-sm"}`}>
        <div className="px-4 md:px-12 py-3 md:py-4 flex items-center justify-between max-w-[1440px] mx-auto">
          <Link to="/" className="z-50">
            <img src="/logo_katzenberg_new.png" alt="Wolfe vom Katzenberg" className="h-10 md:h-14 w-auto drop-shadow-lg" />
          </Link>
          <nav className="hidden lg:flex items-center gap-1" onMouseLeave={() => setHoveredNav(null)}>
            {nav.map(item => {
              const isDropdown = ["Nasze psy", "Mioty", "Galeria", "Wiedza o rasie"].includes(item.name);
              const isActive = item.name === "Nasze psy" ? location.pathname.startsWith("/psy")
                : item.name === "Mioty" ? location.pathname.startsWith("/mioty")
                : item.name === "Galeria" ? location.pathname.startsWith("/galeria")
                : item.name === "Wiedza o rasie" ? (location.pathname === "/owczarek-niemiecki" || location.pathname.startsWith("/anatomia"))
                : item.name === "Z życia hodowli" ? location.pathname.startsWith("/z-zycia-hodowli")
                : location.pathname === item.href;

              const dropdownOpen = item.name === "Nasze psy" ? dogsOpen
                : item.name === "Mioty" ? littersOpen
                : item.name === "Galeria" ? galleryOpen
                : item.name === "Wiedza o rasie" ? wiedzaOpen : false;

              const setDropdown = item.name === "Nasze psy" ? setDogsOpen
                : item.name === "Mioty" ? setLittersOpen
                : item.name === "Galeria" ? setGalleryOpen
                : item.name === "Wiedza o rasie" ? setWiedzaOpen : null;

              return (
                <div
                  key={item.href}
                  className="relative"
                  onMouseEnter={() => {
                    setHoveredNav(item.href);
                    if (setDropdown) setDropdown(true);
                  }}
                  onMouseLeave={() => {
                    if (setDropdown) setDropdown(false);
                  }}
                >
                  <Link
                    to={item.href}
                    className={`relative z-10 px-3 py-1.5 text-[11px] tracking-[0.15em] uppercase transition-colors duration-200 flex items-center gap-1 ${
                      hoveredNav === item.href ? "text-[#0B0B0D]"
                      : isActive ? "text-[#D4AF37]"
                      : "text-[#B8B8B8]"
                    }`}
                    data-testid={`nav-${item.href.replace(/\//g, '') || 'home'}`}
                  >
                    {item.name}
                    {isDropdown && <ChevronDown className={`w-3 h-3 transition-transform duration-200 ${dropdownOpen ? "rotate-180" : ""}`} />}
                  </Link>

                  {/* Sliding gold highlight - grows from small */}
                  {hoveredNav === item.href && (
                    <motion.div
                      layoutId="nav-highlight"
                      className="absolute inset-0 bg-[#D4AF37] rounded-sm origin-center"
                      initial={{ scale: 0, opacity: 0 }}
                      animate={{ scale: 1, opacity: 1 }}
                      transition={{ type: "spring", stiffness: 500, damping: 28, mass: 0.8 }}
                    />
                  )}

                  {/* Dropdown: Nasze psy */}
                  {item.name === "Nasze psy" && (
                    <AnimatePresence>
                      {dogsOpen && (
                        <motion.div
                          initial={{ opacity: 0, y: 8 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: 8 }}
                          transition={{ duration: 0.15 }}
                          className="absolute top-full left-0 mt-3 bg-[#141416] border border-[#2A2A2D] min-w-[200px] py-2 shadow-2xl z-[60]"
                        >
                          <Link to="/psy" className="block px-5 py-2.5 text-[11px] tracking-[0.1em] text-[#B8B8B8] hover:text-[#D4AF37] hover:bg-[#1A1A1D] transition-colors">
                            Wszystkie psy
                          </Link>
                          <div className="h-px bg-[#2A2A2D] mx-4 my-1" />
                          {dogs.map(dog => (
                            <Link key={dog.id} to={`/psy/${dog.id}`}
                              className="block px-5 py-2.5 text-[11px] tracking-[0.1em] text-[#B8B8B8] hover:text-[#D4AF37] hover:bg-[#1A1A1D] transition-colors"
                            >
                              {dog.name} <span className="text-[9px] text-[#B8B8B8]/25 ml-1">{dog.registered_name}</span>
                            </Link>
                          ))}
                        </motion.div>
                      )}
                    </AnimatePresence>
                  )}

                  {/* Dropdown: Mioty */}
                  {item.name === "Mioty" && (
                    <AnimatePresence>
                      {littersOpen && (
                        <motion.div
                          initial={{ opacity: 0, y: 8 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: 8 }}
                          transition={{ duration: 0.15 }}
                          className="absolute top-full left-0 mt-3 bg-[#141416] border border-[#2A2A2D] min-w-[200px] py-2 shadow-2xl z-[60]"
                        >
                          <Link to="/mioty" className="block px-5 py-2.5 text-[11px] tracking-[0.1em] text-[#B8B8B8] hover:text-[#D4AF37] hover:bg-[#1A1A1D] transition-colors">
                            Wszystkie mioty
                          </Link>
                          <div className="h-px bg-[#2A2A2D] mx-4 my-1" />
                          {litters.map(l => (
                            <Link key={l.id} to={`/mioty/${l.slug || l.id}`}
                              className="block px-5 py-2.5 text-[11px] tracking-[0.1em] text-[#B8B8B8] hover:text-[#D4AF37] hover:bg-[#1A1A1D] transition-colors"
                            >{l.name}</Link>
                          ))}
                        </motion.div>
                      )}
                    </AnimatePresence>
                  )}

                  {/* Dropdown: Galeria */}
                  {item.name === "Galeria" && (
                    <AnimatePresence>
                      {galleryOpen && (
                        <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 8 }} transition={{ duration: 0.15 }}
                          className="absolute top-full left-0 mt-3 bg-[#141416] border border-[#2A2A2D] min-w-[160px] py-2 shadow-2xl z-[60]"
                        >
                          <Link to="/galeria" className="block px-5 py-2.5 text-[11px] tracking-[0.1em] text-[#B8B8B8] hover:text-[#D4AF37] hover:bg-[#1A1A1D] transition-colors">Zdjęcia</Link>
                          <div className="h-px bg-[#2A2A2D] mx-4 my-1" />
                          <Link to="/galeria/filmy" className="block px-5 py-2.5 text-[11px] tracking-[0.1em] text-[#B8B8B8] hover:text-[#D4AF37] hover:bg-[#1A1A1D] transition-colors">Filmy</Link>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  )}

                  {/* Dropdown: Wiedza o rasie */}
                  {item.name === "Wiedza o rasie" && (
                    <AnimatePresence>
                      {wiedzaOpen && (
                        <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 8 }} transition={{ duration: 0.15 }}
                          className="absolute top-full right-0 mt-3 bg-[#141416] border border-[#2A2A2D] min-w-[280px] py-2 shadow-2xl z-[60]"
                        >
                          <Link to="/owczarek-niemiecki" className="block px-5 py-2.5 text-[11px] tracking-[0.1em] text-[#B8B8B8] hover:text-[#D4AF37] hover:bg-[#1A1A1D] transition-colors">
                            Charakter rasy
                          </Link>
                          <div className="h-px bg-[#2A2A2D] mx-4 my-1" />
                          <Link to="/anatomia-owczarka-niemieckiego" className="block px-5 py-2.5 text-[11px] tracking-[0.1em] text-[#B8B8B8] hover:text-[#D4AF37] hover:bg-[#1A1A1D] transition-colors">
                            Anatomia owczarka niemieckiego
                          </Link>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  )}
                </div>
              );
            })}
            <a href="https://www.facebook.com/profile.php?id=61593187978675" target="_blank" rel="noopener noreferrer" className="text-[#B8B8B8] hover:text-[#D4AF37] transition-colors ml-4" data-testid="nav-facebook">
              <Facebook className="w-4 h-4" />
            </a>
          </nav>
          <button className="block lg:hidden z-50 p-2 -mr-2" onClick={() => setMenuOpen(!menuOpen)} data-testid="mobile-menu-btn">
            {menuOpen ? <X className="w-7 h-7 text-[#F5F5F5]" /> : <Menu className="w-7 h-7 text-[#F5F5F5]" />}
          </button>
        </div>
      </header>

      {/* Mobile menu */}
      <AnimatePresence>
        {menuOpen && (
          <motion.div initial={{ opacity: 0, x: "100%" }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: "100%" }}
            transition={{ type: "tween", duration: 0.3 }}
            className="fixed inset-0 z-[60] bg-[#0B0B0D] overflow-y-auto"
          >
            {/* Header */}
            <div className="flex items-center justify-between px-4 py-3 border-b border-[#2A2A2D]">
              <img src="/logo_katzenberg_new.png" alt="Wolfe vom Katzenberg" className="h-10 w-auto" />
              <button onClick={() => setMenuOpen(false)} className="w-10 h-10 flex items-center justify-center border border-[#2A2A2D] hover:bg-[#1A1A1D] transition-colors">
                <X className="w-5 h-5 text-[#F5F5F5]" />
              </button>
            </div>
            
            {/* Menu items */}
            <div className="flex flex-col px-6 py-6">
              {nav.map((item, i) => (
                <motion.div 
                  key={item.href} 
                  initial={{ opacity: 0, x: 30 }} 
                  animate={{ opacity: 1, x: 0 }} 
                  transition={{ delay: i * 0.05 }}
                  className="border-b border-[#2A2A2D]"
                >
                  <Link 
                    to={item.href} 
                    onClick={() => setMenuOpen(false)}
                    className={`text-base py-4 font-serif tracking-wider transition-colors flex items-center justify-between ${
                      location.pathname === item.href ? "text-[#D4AF37] font-bold" : "text-[#B8B8B8]"
                    }`}
                  >
                    {item.name}
                    <ArrowRight className="w-4 h-4 text-[#D4AF37]/40" />
                  </Link>
                  {item.name === "Nasze psy" && dogs.length > 0 && (
                    <div className="flex flex-col gap-1 pb-3 pl-4 border-l-2 border-[#D4AF37]/20 ml-2">
                      {dogs.map(dog => (
                        <Link key={dog.id} to={`/psy/${dog.id}`} onClick={() => setMenuOpen(false)}
                          className="text-sm text-[#B8B8B8]/60 hover:text-[#D4AF37] transition-colors py-1"
                        >{dog.name}</Link>
                      ))}
                    </div>
                  )}
                  {item.name === "Galeria" && (
                    <div className="flex flex-col gap-1 pb-3 pl-4 border-l-2 border-[#D4AF37]/20 ml-2">
                      <Link to="/galeria" onClick={() => setMenuOpen(false)} className="text-sm text-[#B8B8B8]/60 hover:text-[#D4AF37] transition-colors py-1">Zdjęcia</Link>
                      <Link to="/galeria/filmy" onClick={() => setMenuOpen(false)} className="text-sm text-[#B8B8B8]/60 hover:text-[#D4AF37] transition-colors py-1">Filmy</Link>
                    </div>
                  )}
                  {item.name === "Mioty" && litters.length > 0 && (
                    <div className="flex flex-col gap-1 pb-3 pl-4 border-l-2 border-[#D4AF37]/20 ml-2">
                      {litters.map(l => (
                        <Link key={l.id} to={`/mioty/${l.slug || l.id}`} onClick={() => setMenuOpen(false)}
                          className="text-sm text-[#B8B8B8]/60 hover:text-[#D4AF37] transition-colors py-1"
                        >{l.name}</Link>
                      ))}
                    </div>
                  )}
                  {item.name === "Wiedza o rasie" && (
                    <div className="flex flex-col gap-1 pb-3 pl-4 border-l-2 border-[#D4AF37]/20 ml-2">
                      <Link to="/owczarek-niemiecki" onClick={() => setMenuOpen(false)} className="text-sm text-[#B8B8B8]/60 hover:text-[#D4AF37] transition-colors py-1">Charakter rasy</Link>
                      <Link to="/anatomia-owczarka-niemieckiego" onClick={() => setMenuOpen(false)} className="text-sm text-[#B8B8B8]/60 hover:text-[#D4AF37] transition-colors py-1">Anatomia owczarka niemieckiego</Link>
                    </div>
                  )}
                </motion.div>
              ))}
            </div>
            
            {/* Footer */}
            <div className="px-6 py-6 border-t border-[#2A2A2D] mt-auto">
              <a href="tel:+48663746095" className="flex items-center gap-2 text-[#B8B8B8]/60 text-sm hover:text-[#D4AF37] transition-colors mb-3">
                <Phone className="w-4 h-4" /> 663 746 095
              </a>
              <div className="flex gap-4 mt-3">
                <a href="https://www.facebook.com/profile.php?id=61593187978675" target="_blank" rel="noopener noreferrer" className="text-[#B8B8B8]/40 hover:text-[#D4AF37] transition-colors">
                  <Facebook className="w-5 h-5" />
                </a>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <main className="pt-[64px]">
        <Outlet context={{ settings, API }} />
      </main>

      {/* Scroll to top button */}
      <AnimatePresence>
        {scrolled && (
          <motion.button
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.8 }}
            onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
            className="fixed bottom-6 right-6 z-50 w-11 h-11 bg-[#D4AF37] text-[#0B0B0D] flex items-center justify-center shadow-lg shadow-[#D4AF37]/20 hover:bg-[#C8A96A] transition-colors duration-300"
            data-testid="scroll-to-top"
          >
            <ChevronUp className="w-5 h-5" />
          </motion.button>
        )}
      </AnimatePresence>

      {/* WhatsApp floating button */}
      <a href="https://wa.me/48663746095" target="_blank" rel="noopener noreferrer"
        className="fixed bottom-6 left-6 z-50 w-14 h-14 bg-[#D4AF37] rounded-full flex items-center justify-center shadow-lg hover:scale-110 transition-transform hover:shadow-[#D4AF37]/30 hover:shadow-xl"
        data-testid="whatsapp-float">
        <svg className="w-7 h-7 text-[#0B0B0D] fill-current" viewBox="0 0 24 24"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
      </a>

      {/* Footer */}
      <footer className="bg-[#141416] border-t border-[#2A2A2D]">
        <div className="px-6 md:px-12 py-16 max-w-7xl mx-auto grid grid-cols-2 md:grid-cols-4 gap-10 md:gap-12">
          {/* Col 1: Hodowla */}
          <div>
            <h4 className="text-[10px] tracking-[0.25em] text-[#D4AF37] mb-5 font-medium">HODOWLA</h4>
            <div className="space-y-2.5 text-sm">
              <Link to="/o-hodowli" className="block text-[#B8B8B8] hover:text-[#F5F5F5] transition-colors">O hodowli</Link>
              <Link to="/psy" className="block text-[#B8B8B8] hover:text-[#F5F5F5] transition-colors">Nasze psy</Link>
              <Link to="/szczenieta" className="block text-[#B8B8B8] hover:text-[#F5F5F5] transition-colors">Szczenięta</Link>
              <Link to="/mioty" className="block text-[#B8B8B8] hover:text-[#F5F5F5] transition-colors">Mioty</Link>
              <Link to="/galeria" className="block text-[#B8B8B8] hover:text-[#F5F5F5] transition-colors">Zdjęcia</Link>
              <Link to="/galeria/filmy" className="block text-[#B8B8B8] hover:text-[#F5F5F5] transition-colors">Filmy</Link>
              <Link to="/z-zycia-hodowli" className="block text-[#B8B8B8] hover:text-[#F5F5F5] transition-colors">Z życia hodowli</Link>
              <Link to="/kontakt" className="block text-[#B8B8B8] hover:text-[#F5F5F5] transition-colors">Kontakt</Link>
            </div>
          </div>

          {/* Col 2: Wiedza o rasie (SEO) */}
          <div>
            <h4 className="text-[10px] tracking-[0.25em] text-[#D4AF37] mb-5 font-medium">WIEDZA O RASIE</h4>
            <div className="space-y-2.5 text-sm">
              <Link to="/owczarek-niemiecki" className="block text-[#B8B8B8] hover:text-[#F5F5F5] transition-colors">Owczarek niemiecki — charakter rasy</Link>
              <Link to="/owczarek-niemiecki#hodowla" className="block text-[#B8B8B8] hover:text-[#F5F5F5] transition-colors">Jak wybrać hodowlę owczarka niemieckiego</Link>
              <Link to="/owczarek-niemiecki#charakter" className="block text-[#B8B8B8] hover:text-[#F5F5F5] transition-colors">Socjalizacja szczeniąt</Link>
              <Link to="/owczarek-niemiecki#ekspert" className="block text-[#B8B8B8] hover:text-[#F5F5F5] transition-colors">Badania HD i ED</Link>
              <Link to="/owczarek-niemiecki#poradnik" className="block text-[#B8B8B8] hover:text-[#F5F5F5] transition-colors">Linia użytkowa i wystawowa</Link>
              <Link to="/anatomia-owczarka-niemieckiego" className="block text-[#B8B8B8] hover:text-[#F5F5F5] transition-colors">Anatomia owczarka niemieckiego</Link>
            </div>
          </div>

          {/* Col 3: Informacje (Legal) */}
          <div>
            <h4 className="text-[10px] tracking-[0.25em] text-[#D4AF37] mb-5 font-medium">INFORMACJE</h4>
            <div className="space-y-2.5 text-sm">
              <Link to="/polityka-prywatnosci" className="block text-[#B8B8B8] hover:text-[#F5F5F5] transition-colors">Polityka prywatności</Link>
              <Link to="/rodo" className="block text-[#B8B8B8] hover:text-[#F5F5F5] transition-colors">RODO</Link>
              <Link to="/regulamin" className="block text-[#B8B8B8] hover:text-[#F5F5F5] transition-colors">Regulamin strony</Link>
              <Link to="/warunki-rezerwacji" className="block text-[#B8B8B8] hover:text-[#F5F5F5] transition-colors">Warunki rezerwacji szczenięcia</Link>
              <Link to="/cookies" className="block text-[#B8B8B8] hover:text-[#F5F5F5] transition-colors">Cookies</Link>
            </div>
          </div>

          {/* Col 4: Kontakt */}
          <div>
            <h4 className="text-[10px] tracking-[0.25em] text-[#D4AF37] mb-5 font-medium">KONTAKT</h4>
            <p className="text-[#B8B8B8]/60 text-xs leading-relaxed mb-4">Hodowla Owczarka Niemieckiego<br />Wölfe vom Katzenberg</p>
            <div className="space-y-3 text-sm text-[#B8B8B8]">
              <a href="tel:+48663746095" className="flex items-center gap-2 hover:text-[#F5F5F5] transition-colors"><Phone className="w-4 h-4 text-[#C8A96A]" /> +48 663 746 095</a>
              <a href="mailto:katzenberg.hodowla@gmail.com" className="flex items-center gap-2 hover:text-[#F5F5F5] transition-colors"><Mail className="w-4 h-4 text-[#C8A96A]" /> katzenberg.hodowla@gmail.com</a>
              <div className="h-px bg-[#2A2A2D] my-1" />
              <a href="https://www.facebook.com/profile.php?id=61593187978675" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 hover:text-[#F5F5F5] transition-colors"><Facebook className="w-4 h-4 text-[#C8A96A]" /> Facebook</a>
              <a href="https://instagram.com/wolfevomkatzenberg" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 hover:text-[#F5F5F5] transition-colors"><Instagram className="w-4 h-4 text-[#C8A96A]" /> Instagram</a>
            </div>
          </div>
        </div>
        <div className="border-t border-[#2A2A2D] px-6 md:px-12 py-5 flex flex-col sm:flex-row justify-between items-center text-[10px] text-[#B8B8B8]/40 max-w-7xl mx-auto gap-2">
          <span>&copy; 2026 Wolfe vom Katzenberg. Wszelkie prawa zastrzeżone. Kopiowanie, rozpowszechnianie oraz wykorzystywanie treści, zdjęć i elementów graficznych bez pisemnej zgody właściciela jest zabronione.</span>
          <div className="flex items-center gap-4">
            <Link to="/admin/opinie" className="hover:text-[#D4AF37] transition-colors flex items-center gap-1">
              <Settings className="w-3 h-3" />
            </Link>
            <span>Made with love <a href="https://apisdesign.pl" target="_blank" rel="noopener noreferrer" className="hover:text-[#D4AF37] transition-colors">apisdesign.pl</a></span>
          </div>
        </div>
      </footer>

      {/* Cookie Banner RODO */}
      <AnimatePresence>
        {!cookieAccepted && (
          <motion.div
            initial={{ y: 100, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 100, opacity: 0 }}
            transition={{ type: "spring", damping: 25, stiffness: 300 }}
            className="fixed bottom-0 left-0 right-0 z-[70] bg-[#141416] border-t border-[#2A2A2D] px-4 md:px-8 py-4"
            data-testid="cookie-banner"
          >
            <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
              <p className="text-[#B8B8B8] text-xs sm:text-sm leading-relaxed text-center sm:text-left">
                Ta strona korzysta z plików cookies w celu zapewnienia najlepszej jakości usług. Korzystając ze strony wyrażasz zgodę na ich używanie zgodnie z{" "}
                <Link to="/polityka-prywatnosci" className="text-[#D4AF37] hover:underline">Polityką Prywatności</Link> i{" "}
                <Link to="/rodo" className="text-[#D4AF37] hover:underline">RODO</Link>.
              </p>
              <div className="flex gap-3 shrink-0">
                <button
                  onClick={() => { localStorage.setItem("cookies_accepted", "true"); setCookieAccepted(true); }}
                  className="px-6 py-2 bg-[#D4AF37] text-[#0B0B0D] text-xs tracking-[0.1em] uppercase font-medium hover:bg-[#C8A96A] transition-colors"
                  data-testid="cookie-accept-btn"
                >
                  Akceptuję
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default KennelLayout;
